import React from 'react';
import './TopReasonsChart.scss';
import { FunnelReason, FunnelUnitLossTotal } from '../../interfaces/panel-data';
import { unitLabel } from '../../utils/format-unit';
import { formatExactCount } from '../../utils/format-count';
import { CountValue } from './CountValue';

interface TopReasonsChartProps {
  title: string;
  reasons: FunnelReason[];
  /**
   * The capability's total loss in a single unit, when its sub-stages agree on one. Used only as the
   * denominator for the share each reason accounts for — see `shareOfUnitLoss`. Absent whenever the
   * capability spans units, which is exactly when no such share exists to state.
   */
  unitLoss?: FunnelUnitLossTotal | null;
  /** `neutral` drops the severity bands: filtered reasons are not loss, so they get no alarm colour. */
  tone?: 'severity' | 'neutral';
}

/** Heading for reasons whose stage has no configured unit — never an invented one like "items". */
const UNSPECIFIED_UNIT = 'Unspecified unit';

/**
 * Where the bar's colour changes, as a share of the longest bar in the group.
 *
 * At or above `HIGH` is red, at or above `MEDIUM` is orange, below it amber. Boundaries are inclusive at
 * the lower edge, so an exact 80 is red and an exact 30 orange.
 */
const FILL_BAND_HIGH = 80;
const FILL_BAND_MEDIUM = 30;

type FillBand = 'high' | 'medium' | 'low';

/** The colour band for a bar of the given rendered length. */
const fillBand = (widthPercent: number): FillBand => {
  if (widthPercent >= FILL_BAND_HIGH) return 'high';
  if (widthPercent >= FILL_BAND_MEDIUM) return 'medium';
  return 'low';
};

interface UnitGroup {
  /** Grouping key: the unit, or the sub-stage code when the stage has no unit configured. */
  key: string;
  heading: string;
  reasons: FunnelReason[];
  /** Largest count in this group, so bars scale against their own unit and not a shared maximum. */
  maxCount: number;
}

/**
 * Splits reasons into one group per unit, in the order the units are first seen.
 *
 * Unitless reasons key on their sub-stage rather than sharing a bucket, mirroring the backend: pooling
 * them would scale unrelated quantities against each other, which is the comparison being removed.
 */
const groupByUnit = (reasons: FunnelReason[]): UnitGroup[] => {
  const groups: UnitGroup[] = [];
  const byKey = new Map<string, UnitGroup>();

  reasons.forEach((reason) => {
    const label = unitLabel(reason);
    const key = label || `substage:${reason.subStage}`;
    let group = byKey.get(key);
    if (!group) {
      group = { key, heading: label || UNSPECIFIED_UNIT, reasons: [], maxCount: 0 };
      byKey.set(key, group);
      groups.push(group);
    }
    group.reasons.push(reason);
    group.maxCount = Math.max(group.maxCount, reason.count);
  });

  return groups;
};

/**
 * What share of the capability's total loss in this unit the reason accounts for, or null when that
 * cannot be stated truthfully.
 *
 * The denominator is the backend's `unitLoss.lost`, deliberately not the sum of the counts on screen.
 * `FunnelPanelStrategy` emits only the five largest reasons per capability, so summing what is rendered
 * gives the total of the five shown rather than the total lost — and every share computed from it would
 * be overstated, by more the longer the tail. A reason that is 30% of the real loss would read as 60%
 * where the five shown are half of it, which is a specific and plausible-looking falsehood.
 *
 * Null, and therefore no share stated, whenever:
 *  - the capability spans units or has none configured (`unitLoss` absent) — there is no single total to
 *    take a share of, and taking a share of a mixed sum of pages and SKUs would mean nothing;
 *  - the group's unit is not the unit `unitLoss` is denominated in — the two are then measuring
 *    different things, and the one is not a share of the other;
 *  - the total is zero or negative, which no share divides into;
 *  - the reason's own count exceeds the total, which is a contradiction in the payload rather than a
 *    share above 100%.
 */
const shareOfUnitLoss = (
  count: number,
  group: UnitGroup,
  unitLoss: FunnelUnitLossTotal | null | undefined
): number | null => {
  if (!unitLoss || !Number.isFinite(unitLoss.lost) || unitLoss.lost <= 0) return null;
  if (unitLabel(unitLoss) !== unitLabel(group.reasons[0])) return null;
  if (!Number.isFinite(count) || count < 0 || count > unitLoss.lost) return null;
  return (count / unitLoss.lost) * 100;
};

/** The share to one decimal, dropping a trailing `.0` so a clean 40% does not read as `40.0%`. */
const formatShare = (share: number): string => `${parseFloat(share.toFixed(1))}%`;

/**
 * Reason counts as horizontal bars, grouped by the unit they count in.
 *
 * The grouping is the point. Bars were previously scaled against a single maximum across every reason,
 * so a 400-page bar rendered at two-thirds the length of a 610-SKU bar — a visual comparison of pages
 * against SKUs, which says nothing true. Each unit now scales against its own maximum, and each count
 * states its unit so "1,247" reads as "1,247 pages".
 *
 * With one unit no sub-heading appears: there is nothing to disambiguate, and labelling every
 * single-unit chart would be a regression in the common case for the sake of the rare one.
 */
export const TopReasonsChart: React.FC<TopReasonsChartProps> = ({
  title,
  reasons,
  unitLoss,
  tone = 'severity',
}) => {
  if (!reasons || reasons.length === 0) return null;

  const groups = groupByUnit(reasons);
  const showHeadings = groups.length > 1;

  return (
    <div className="ovp-reasons">
      <div className="ovp-reasons__title">{title}</div>
      {groups.map((group) => (
        <div key={group.key} className="ovp-reasons__group">
          {showHeadings && (
            <div className="ovp-reasons__unit" data-testid="reasons-unit-heading">
              {group.heading}
            </div>
          )}
          {/* Keyed on sub-stage and reason both, since the same reason can be recorded against several
              sub-stages — keying on the reason alone gives React duplicate keys. */}
          {group.reasons.map((r) => {
            // maxCount is 0 when every reason in this group has a zero count; dividing would give
            // width: NaN%, which silently renders nothing at all.
            const width = group.maxCount > 0 ? (r.count / group.maxCount) * 100 : 0;
            const share = shareOfUnitLoss(r.count, group, unitLoss);
            const unit = unitLabel(r);
            /* Count first with the share in brackets, matching how the sub-stage rows above already read
               ("139 pages lost (1%)") — the same shape means a reader does not have to work out which
               figure is which twice on one panel.
               On the bar rather than the label, because the bar is what the share qualifies: the length is
               a share of the worst reason, and this says what share of the whole loss that amounts to. The
               denominator is spelled out rather than left implicit, because "82% as long as the worst bar"
               and "40% of all pages lost" are different figures, and a bare "40%" beside a bar of another
               length invites reading it as the length. Absent, the bar carries no tooltip at all rather
               than an empty one. */
            const exactCount = unit
              ? `${formatExactCount(r.count)} ${unit}`
              : formatExactCount(r.count);
            const barTooltip =
              share !== null
                ? `${exactCount} (${formatShare(share)} of ${formatExactCount(
                    (unitLoss as FunnelUnitLossTotal).lost
                  )} lost in this capability)`
                : undefined;

            return (
              <div key={`${r.subStage}-${r.reason}`} className="ovp-reasons__row">
                {/* Clipped by the column width, and the identifying tail of a reason code is the first
                    thing an ellipsis takes — so the whole label stays reachable on hover. */}
                <span
                  className="ovp-reasons__label"
                  title={`${r.displayName} (${r.subStageDisplayName})`}
                >
                  {r.displayName} <small className="ovp-reasons__sub">({r.subStageDisplayName})</small>
                </span>
                <div className="ovp-reasons__track" title={barTooltip}>
                  <span
                    className={`ovp-reasons__fill ovp-reasons__fill--${
                      tone === 'neutral' ? 'neutral' : fillBand(width)
                    }`}
                    style={{ width: `${width}%` }}
                    data-testid="reasons-fill"
                  />
                </div>
                <span className="ovp-reasons__count">
                  <CountValue count={r.count} text={r} />
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};
